import type { GoogleSignInError } from '@sbc/domain-models/admin';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useParams,
} from '@tanstack/react-router';

import { AppShell } from '@/components/app-shell';
import { parseGoogleSignInError } from '@/lib/auth/google-sign-in';
import { RequireAuth } from '@/lib/auth/require-auth';
import { safeReturnTo } from '@/lib/auth/return-to';
import { AgentDetailPage } from '@/routes/agents/agent-detail';
import { AgentEditPage } from '@/routes/agents/agent-edit';
import { AgentNewPage } from '@/routes/agents/agent-new';
import { AgentsListPage } from '@/routes/agents/agents-list';
import { EvalsGuidePage } from '@/routes/evals/evals-guide';
import { RunDetailPage } from '@/routes/evals/run-detail';
import { RunsListPage } from '@/routes/evals/runs-list';
import { SuiteDetailPage } from '@/routes/evals/suite-detail';
import { SuiteNewPage } from '@/routes/evals/suite-new';
import { SuitesListPage } from '@/routes/evals/suites-list';
import { TaskEditPage } from '@/routes/evals/task-edit';
import { TaskNewPage } from '@/routes/evals/task-new';
import { FeedbackPage } from '@/routes/feedback';
import { ForgotPasswordPage } from '@/routes/forgot-password';
import { BindingEditPage } from '@/routes/gateway/binding-edit';
import { BindingNewPage } from '@/routes/gateway/binding-new';
import { ConnectionDetailPage } from '@/routes/gateway/connection-detail';
import { ConnectionEditPage } from '@/routes/gateway/connection-edit';
import { ConnectionNewPage } from '@/routes/gateway/connection-new';
import { ConnectionsListPage, parseConnectionsSearch } from '@/routes/gateway/connections-list';
import { DraftShadowPage, parseDraftShadowSearch } from '@/routes/gateway/draft-shadow';
import { FacebookGroupLiveViewPage } from '@/routes/gateway/facebook-group-live-view';
import { AgentRunsListPage, parseRunsListSearch } from '@/routes/gateway/runs-list';
import { parseInboxSearch } from '@/routes/inbox/inbox-search';
import { InboxWorkspace } from '@/routes/inbox/inbox-workspace';
import { ArticleEditPage } from '@/routes/knowledge-base/article-edit';
import { ArticleNewPage } from '@/routes/knowledge-base/article-new';
import { GlossaryProposalPage } from '@/routes/knowledge-base/glossary-proposal';
import { GlossaryProposalsPage } from '@/routes/knowledge-base/glossary-proposals';
import {
  isStatusFilter,
  KbAllArticlesPage,
  type KbAllArticlesSearch,
} from '@/routes/knowledge-base/kb-all-articles';
import { KbBrowsePage } from '@/routes/knowledge-base/kb-browse';
import { LoginPage } from '@/routes/login';
import { McpAuthorizePage } from '@/routes/mcp/authorize';
import { McpConnectionDetailPage } from '@/routes/mcp/connection-detail';
import { McpConnectionEditPage } from '@/routes/mcp/connection-edit';
import { McpConnectionNewPage } from '@/routes/mcp/connection-new';
import { McpConnectionsListPage } from '@/routes/mcp/connections-list';
import { OutcomesListPage } from '@/routes/outcomes/outcomes-list';
import { parseOutcomesListSearch } from '@/routes/outcomes/outcomes-search';
import { ResetPasswordPage } from '@/routes/reset-password';
import { ProposalsListPage } from '@/routes/review/proposals-list';
import { ReviewChatPage } from '@/routes/review/review-chat';
import { ReviewOpenPage } from '@/routes/review/review-open';
import { SessionDetailPage } from '@/routes/sessions/session-detail';
import { SessionNewPage } from '@/routes/sessions/session-new';
import { SessionsListPage } from '@/routes/sessions/sessions-list';
import { LocalToolsPage } from '@/routes/tools/local-tools';
import { UserEditPage } from '@/routes/users/user-edit';
import { UserNewPage } from '@/routes/users/user-new';
import { UsersListPage } from '@/routes/users/users-list';

import { ModelsPage } from './routes/models/models-page';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Pathless layout route: every authenticated page renders inside the dashboard
// shell behind the auth guard.
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: () => (
    <RequireAuth>
      <AppShell>
        <Outlet />
      </AppShell>
    </RequireAuth>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/sessions' });
  },
});

const usersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users',
  component: UsersListPage,
});

const userNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users/new',
  component: UserNewPage,
});

const userEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users/$userId/edit',
  component: function UserEditRoute() {
    const { userId } = userEditRoute.useParams();
    return <UserEditPage userId={userId} />;
  },
});

const agentsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/agents',
  component: AgentsListPage,
});

const agentNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/agents/new',
  component: AgentNewPage,
});

const agentDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/agents/$agentId',
  component: function AgentDetailRoute() {
    const { agentId } = agentDetailRoute.useParams();
    return <AgentDetailPage agentId={agentId} />;
  },
});

const agentEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/agents/$agentId/edit',
  component: function AgentEditRoute() {
    const { agentId } = agentEditRoute.useParams();
    return <AgentEditPage agentId={agentId} />;
  },
});

const modelsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/models',
  component: ModelsPage,
});

const sessionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/sessions',
  component: SessionsListPage,
});

const sessionNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/sessions/new',
  component: SessionNewPage,
});

const sessionDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/sessions/$sessionId',
  component: function SessionDetailRoute() {
    const { sessionId } = sessionDetailRoute.useParams();
    return <SessionDetailPage sessionId={sessionId} />;
  },
});

const inboxRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/inbox',
  validateSearch: parseInboxSearch,
  component: function InboxRoute() {
    const search = inboxRoute.useSearch();
    const { conversationId } = useParams({ strict: false });
    return <InboxWorkspace search={search} conversationId={conversationId} />;
  },
});

const inboxConversationRoute = createRoute({
  getParentRoute: () => inboxRoute,
  path: '$conversationId',
});

const connectionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gateway/connections',
  validateSearch: parseConnectionsSearch,
  component: ConnectionsListPage,
});

const connectionNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gateway/connections/new',
  component: ConnectionNewPage,
});

const connectionDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gateway/connections/$connectionId',
  component: function ConnectionDetailRoute() {
    const { connectionId } = connectionDetailRoute.useParams();
    return <ConnectionDetailPage connectionId={connectionId} />;
  },
});

const connectionEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gateway/connections/$connectionId/edit',
  component: function ConnectionEditRoute() {
    const { connectionId } = connectionEditRoute.useParams();
    return <ConnectionEditPage connectionId={connectionId} />;
  },
});

const connectionLiveViewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gateway/connections/$connectionId/live',
  component: function ConnectionLiveViewRoute() {
    const { connectionId } = connectionLiveViewRoute.useParams();
    return <FacebookGroupLiveViewPage connectionId={connectionId} />;
  },
});

const bindingNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gateway/bindings/new',
  // Optional ?connectionId= prefills the connection select (from a connection's "Add binding").
  validateSearch: (search: Record<string, unknown>): { connectionId?: string } => ({
    connectionId: typeof search.connectionId === 'string' ? search.connectionId : undefined,
  }),
  component: function BindingNewRoute() {
    const { connectionId } = bindingNewRoute.useSearch();
    return <BindingNewPage connectionId={connectionId} />;
  },
});

const bindingEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gateway/bindings/$bindingId/edit',
  component: function BindingEditRoute() {
    const { bindingId } = bindingEditRoute.useParams();
    return <BindingEditPage bindingId={bindingId} />;
  },
});

const agentRunsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gateway/runs',
  validateSearch: parseRunsListSearch,
  component: function AgentRunsListRoute() {
    const search = agentRunsRoute.useSearch();
    return <AgentRunsListPage search={search} />;
  },
});

const draftShadowRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gateway/draft-shadow',
  validateSearch: parseDraftShadowSearch,
  component: function DraftShadowRoute() {
    const search = draftShadowRoute.useSearch();
    return <DraftShadowPage search={search} />;
  },
});

const outcomesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/outcomes',
  validateSearch: parseOutcomesListSearch,
  component: function OutcomesListRoute() {
    const search = outcomesRoute.useSearch();
    return <OutcomesListPage search={search} />;
  },
});

const mcpConnectionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mcp/connections',
  component: McpConnectionsListPage,
});

const mcpConnectionNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mcp/connections/new',
  component: McpConnectionNewPage,
});

const mcpConnectionDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mcp/connections/$connectionId',
  component: function McpConnectionDetailRoute() {
    const { connectionId } = mcpConnectionDetailRoute.useParams();
    return <McpConnectionDetailPage connectionId={connectionId} />;
  },
});

const mcpConnectionEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mcp/connections/$connectionId/edit',
  component: function McpConnectionEditRoute() {
    const { connectionId } = mcpConnectionEditRoute.useParams();
    return <McpConnectionEditPage connectionId={connectionId} />;
  },
});

// The MCP approval page. Behind the same guard as every other page, and it survives
// the bounce to /login because RequireAuth carries a return-to.
const mcpAuthorizeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mcp/authorize',
  validateSearch: (search: Record<string, unknown>): { request_id?: string } => ({
    request_id: typeof search.request_id === 'string' ? search.request_id : undefined,
  }),
  component: function McpAuthorizeRoute() {
    const { request_id: requestId } = mcpAuthorizeRoute.useSearch();
    return <McpAuthorizePage requestId={requestId ?? ''} />;
  },
});

const toolsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tools',
  component: LocalToolsPage,
});

const evalsGuideRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/evals/guide',
  component: EvalsGuidePage,
});

const evalSuitesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/evals/suites',
  component: SuitesListPage,
});

const evalSuiteNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/evals/suites/new',
  component: SuiteNewPage,
});

const evalSuiteDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/evals/suites/$suiteId',
  component: function EvalSuiteDetailRoute() {
    const { suiteId } = evalSuiteDetailRoute.useParams();
    return <SuiteDetailPage suiteId={suiteId} />;
  },
});

const evalTaskNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/evals/suites/$suiteId/tasks/new',
  component: function EvalTaskNewRoute() {
    const { suiteId } = evalTaskNewRoute.useParams();
    return <TaskNewPage suiteId={suiteId} />;
  },
});

const evalTaskEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/evals/suites/$suiteId/tasks/$taskId',
  component: function EvalTaskEditRoute() {
    const { suiteId, taskId } = evalTaskEditRoute.useParams();
    return <TaskEditPage suiteId={suiteId} taskId={taskId} />;
  },
});

const evalRunsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/evals/runs',
  component: RunsListPage,
});

const evalRunDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/evals/runs/$runId',
  component: function EvalRunDetailRoute() {
    const { runId } = evalRunDetailRoute.useParams();
    return <RunDetailPage key={runId} runId={runId} />;
  },
});

const knowledgeBaseRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/knowledge-base',
  component: KbBrowsePage,
});

const knowledgeAllArticlesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/knowledge-base/all',
  // The flat "All articles" view is the approval queue: an absent status defaults
  // to draft (handled in the page), and owner/status/search live in the URL so a
  // filtered view survives the round-trip to an article edit and the back button.
  validateSearch: (search: Record<string, unknown>): KbAllArticlesSearch => ({
    owner: typeof search.owner === 'string' ? search.owner : undefined,
    status:
      typeof search.status === 'string' && isStatusFilter(search.status)
        ? search.status
        : undefined,
    q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
  }),
  component: function KnowledgeAllArticlesRoute() {
    const { owner, status, q } = knowledgeAllArticlesRoute.useSearch();
    return <KbAllArticlesPage owner={owner} status={status} q={q} />;
  },
});

const knowledgeArticleNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/knowledge-base/articles/new',
  // Optional ?folderId= prefills the folder select (from a folder's "New article").
  validateSearch: (search: Record<string, unknown>): { folderId?: string } => ({
    folderId: typeof search.folderId === 'string' ? search.folderId : undefined,
  }),
  component: function KnowledgeArticleNewRoute() {
    const { folderId } = knowledgeArticleNewRoute.useSearch();
    return <ArticleNewPage folderId={folderId} />;
  },
});

const knowledgeArticleEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/knowledge-base/articles/$articleId/edit',
  component: function KnowledgeArticleEditRoute() {
    const { articleId } = knowledgeArticleEditRoute.useParams();
    return <ArticleEditPage articleId={articleId} />;
  },
});

const glossaryProposalsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/knowledge-base/glossary-proposals',
  component: GlossaryProposalsPage,
});

const glossaryProposalRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/knowledge-base/glossary-proposals/$proposalId',
  component: function GlossaryProposalRoute() {
    const { proposalId } = glossaryProposalRoute.useParams();
    return <GlossaryProposalPage proposalId={proposalId} />;
  },
});

const reviewOpenRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/review',
  component: ReviewOpenPage,
});

const reviewProposalsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/review/proposals',
  component: ProposalsListPage,
});

const reviewChatRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/review/$sessionId',
  component: function ReviewChatRoute() {
    const { sessionId } = reviewChatRoute.useParams();
    return <ReviewChatPage sessionId={sessionId} />;
  },
});

const feedbackRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/feedback',
  component: FeedbackPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // `redirect` is narrowed here, once, to a path on this origin — the login page then
  // treats it as trusted.
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; googleError?: GoogleSignInError } => ({
    redirect: safeReturnTo(search.redirect),
    googleError: parseGoogleSignInError(search.googleError),
  }),
  component: function LoginRoute() {
    const { redirect: returnTo, googleError } = loginRoute.useSearch();
    return <LoginPage redirect={returnTo} googleError={googleError} />;
  },
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

const routeTree = rootRoute.addChildren([
  appLayoutRoute.addChildren([
    indexRoute,
    usersRoute,
    userNewRoute,
    userEditRoute,
    agentsRoute,
    agentNewRoute,
    agentDetailRoute,
    agentEditRoute,
    modelsRoute,
    sessionsRoute,
    sessionNewRoute,
    sessionDetailRoute,
    inboxRoute.addChildren([inboxConversationRoute]),
    connectionsRoute,
    connectionNewRoute,
    connectionDetailRoute,
    connectionEditRoute,
    connectionLiveViewRoute,
    bindingNewRoute,
    bindingEditRoute,
    agentRunsRoute,
    draftShadowRoute,
    outcomesRoute,
    mcpConnectionsRoute,
    mcpConnectionNewRoute,
    mcpConnectionDetailRoute,
    mcpConnectionEditRoute,
    mcpAuthorizeRoute,
    toolsRoute,
    evalsGuideRoute,
    evalSuitesRoute,
    evalSuiteNewRoute,
    evalSuiteDetailRoute,
    evalTaskNewRoute,
    evalTaskEditRoute,
    evalRunsRoute,
    evalRunDetailRoute,
    knowledgeBaseRoute,
    knowledgeAllArticlesRoute,
    knowledgeArticleNewRoute,
    knowledgeArticleEditRoute,
    glossaryProposalsRoute,
    glossaryProposalRoute,
    reviewOpenRoute,
    reviewProposalsRoute,
    reviewChatRoute,
    feedbackRoute,
  ]),
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
